// frontend/src/pages/VerifyEmail.tsx

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Zap } from 'lucide-react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';

type Status = 'verifying' | 'success' | 'error';

export function VerifyEmail() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [status, setStatus] = useState<Status>(token ? 'verifying' : 'error');
  // Token is single-use: guard against StrictMode running the effect twice
  const requested = useRef(false);

  useEffect(() => {
    if (!token || requested.current) return;
    requested.current = true;

    api
      .get('/auth/verify-email', { params: { token } })
      .then(() => setStatus('success'))
      .catch(() => setStatus('error'));
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-apple-blue-50 to-white py-12 px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <Zap className="w-8 h-8 text-apple-blue-500" />
          <span className="text-2xl font-semibold">{t('app_name')}</span>
        </Link>

        <Card>
          <CardHeader>
            <CardTitle className="text-center text-3xl">{t('auth.verify_email_title')}</CardTitle>
          </CardHeader>

          <CardContent>
            <div className="text-center space-y-4">
              {status === 'verifying' && (
                <p className="text-apple-gray-700">{t('auth.verifying_email')}</p>
              )}
              {status === 'success' && (
                <p className="text-green-700">{t('auth.email_verified')}</p>
              )}
              {status === 'error' && (
                <p className="text-red-700">{t('auth.verification_failed')}</p>
              )}
              {status !== 'verifying' && (
                <Link
                  to="/login"
                  className="inline-block text-apple-blue-500 hover:text-apple-blue-600 font-medium"
                >
                  {t('auth.back_to_login')}
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
